import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChefHat,
  Home,
  MapPin,
  Package,
  StickyNote,
  Table2,
  Truck,
  Users,
  UtensilsCrossed,
  LayoutGrid,
} from "lucide-react";
import { useLocale } from "@/contexts/LocaleProvider";
import { cn } from "@/lib/utils";
import "@/styles/simple-ui.css";

const MODULES = [
  { to: "/masters/menu-item/list", labelKey: "nav.menuItem", hintKey: "simple.mastersProductsHint", icon: Package, bg: "bg-[#F97316]" },
  { to: "/masters/main-menu", labelKey: "nav.mainMenu", hintKey: "simple.mastersCategoryHint", icon: LayoutGrid, bg: "bg-[#2563EB]" },
  { to: "/masters/sub-menu", labelKey: "nav.subMenu", hintKey: "simple.mastersSubHint", icon: UtensilsCrossed, bg: "bg-[#0EA5E9]" },
  { to: "/masters/tables", labelKey: "nav.tables", hintKey: "simple.mastersTablesHint", icon: Table2, bg: "bg-[#059669]" },
  { to: "/masters/customers", labelKey: "nav.customers", hintKey: "simple.mastersCustomersHint", icon: Users, bg: "bg-[#7C3AED]" },
  { to: "/masters/suppliers", labelKey: "nav.suppliers", hintKey: "simple.mastersSuppliersHint", icon: Truck, bg: "bg-[#D97706]" },
  { to: "/masters/delivery-areas", labelKey: "nav.deliveryAreas", hintKey: "simple.mastersAreasHint", icon: MapPin, bg: "bg-[#0D9488]" },
  { to: "/masters/delivery-persons", labelKey: "nav.deliveryPersons", hintKey: "simple.mastersDriversHint", icon: Truck, bg: "bg-[#DC2626]" },
  { to: "/masters/kitchen-notes", labelKey: "nav.kitchenNotes", hintKey: "simple.mastersNotesHint", icon: StickyNote, bg: "bg-[#CA8A04]" },
  { to: "/masters/kot", labelKey: "nav.kotMaster", hintKey: "simple.mastersKotHint", icon: ChefHat, bg: "bg-[#475569]" },
];

export default function SimpleMastersHub() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-masters-hub">
      <div className="flex items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <h1 className="text-lg font-extrabold text-slate-900">{t("nav.masters")}</h1>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p className="mb-4 text-center text-sm text-slate-500 sm:text-start">
          {t("simple.mastersSubtitle", "Choose what you want to manage")}
        </p>
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {MODULES.map((mod) => {
            const Icon = mod.icon;
            return (
              <Link
                key={mod.to}
                to={mod.to}
                className={cn("simple-tile flex min-h-[120px] flex-col items-center justify-center gap-2 p-4 text-center text-white", mod.bg)}
                data-testid={`simple-masters-${mod.to.replace(/\//g, "-")}`}
              >
                <Icon className="h-8 w-8" strokeWidth={2.25} />
                <span className="text-[15px] font-extrabold leading-tight">{t(mod.labelKey)}</span>
                <span className="text-[11px] font-medium text-white/85">{t(mod.hintKey)}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
